import React, {useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {NamedColors} from 'src/designSystem/colors'
import {BottomSheet} from 'src/designSystem/components/atoms/BottomSheet/BottomSheet'
import Box from 'src/designSystem/components/atoms/Box/Box'
import Button from 'src/designSystem/components/atoms/Button/Button'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import {SvgIcon} from 'src/designSystem/components/atoms/SvgIcon/SvgIcon'

export type AdvanceAvailableSubTileProps = {
  testID: string
  onGetAdvance: () => Promise<void>
  preQualAmount: number | string
  assessmentDate?: string
}

const AdvanceAvailableSubTile: React.FC<AdvanceAvailableSubTileProps> = ({
  testID,
  onGetAdvance: handleOnGetAdvance,
  preQualAmount,
  assessmentDate,
}) => {
  const {t} = useTranslation('DashboardMembershipCanceledTile')
  const [showReactivateMembership, setShowReactivateMembership] = useState(false)

  const handleGetAdvanceOnDismiss = useCallback(() => {
    return handleOnGetAdvance().then(() => {
      setShowReactivateMembership(false)
    })
  }, [handleOnGetAdvance])

  return (
    <>
      <Box direction="column" gap="small" testID={testID}>
        <Box direction="column" gap="little">
          <Box
            padding="tiny"
            radius="little"
            background={NamedColors.GOLD}
            shrink={1}
            alignSelf="start"
          >
            <PFText variant="label_sm">{t('DontMissOut')}</PFText>
          </Box>
          <PFText variant="p_lg_semibold">{t('YouHaveAnAdvanceAvailable')}</PFText>
          <PFText variant="p">{t('AllAdvancesComeWithFreeInstantTransfer')}</PFText>
          <PFText variant="d3">{`$${preQualAmount}`}</PFText>
        </Box>
        <Box fill="horizontal" direction="column" gap="small">
          <Button
            mode="primary"
            size="medium"
            onPress={() => setShowReactivateMembership(true)}
            width="100%"
            testID={`${testID}-Get-Advance-Btn`}
          >
            {t('GetAdvance')}
          </Button>
          {assessmentDate ? (
            <PFText variant="p_sm" color={NamedColors.SILVER} textAlign="center">
              {t('AdvanceAmountLastUpdatedAt', {time: assessmentDate})}
            </PFText>
          ) : null}
        </Box>
      </Box>
      <BottomSheet
        visible={showReactivateMembership}
        closeButtonAction={() => setShowReactivateMembership(false)}
        onDismiss={handleGetAdvanceOnDismiss}
        dismissButton={{
          text: t('ReactivateMembership'),
          testID: `${testID}-Reactivate-Membership-Btn`,
        }}
        showDots={false}
        showCloseButton
        title={<SvgIcon name="lock" colorVariant={'info'} size="large" />}
        testID={`${testID}-Reactivate-Membership-BottomSheet`}
      >
        <Box gap="small">
          <PFText variant="h3">{t('ReactivateMembershipToUnlockThisAdvance')}</PFText>
          <PFText variant="p">{t('ReactivateYourMembershipInJustAFewSteps')}</PFText>
        </Box>
      </BottomSheet>
    </>
  )
}

export {AdvanceAvailableSubTile}
